(function () {
  // State persisted across re-renders (variant / colour changes)
  let savedSeatValue = null;
  let savedBarHandle = null;
  const savedBarSelections = new Map();
  const barCache = new Map(); // handlebar JSON promises — cached to avoid re-fetching
  let reinitTimer    = null;

  // Staff-facing line-item value (same Spanish wording as the other set properties).
  const NO_BAR_LABEL = 'Sin manillar';

  // ── helpers ────────────────────────────────────────────────────────────────
  // Native radio of the "Sin manillar" option value, in the same product section as the widget.
  const findNoBarRadio = (root) => {
    const id = root && root.dataset.noHandlebarValueId;
    if (!id) return null;
    const scope = root.closest('[id^="shopify-section"]') || document;
    return scope.querySelector(`.js-option[data-value-id="${id}"]`);
  };
  const fetchProduct = async (handle) => {
    let product = null;
    const variants = [];
    const seen = new Set();

    for (let page = 1; page <= 20; page += 1) {
      const path = `/products/${encodeURIComponent(handle)}.js?limit=250&page=${page}`;
      const r = await fetch(path);
      if (!r.ok) throw new Error(`Cannot load ${path}`);
      const current = await r.json();
      if (!product) product = current;

      const pageVariants = Array.isArray(current.variants) ? current.variants : [];
      let added = 0;
      pageVariants.forEach(variant => {
        const id = String(variant.id);
        if (seen.has(id)) return;
        seen.add(id);
        variants.push(variant);
        added += 1;
      });

      if (pageVariants.length < 250 || added === 0) break;
    }

    if (!product) throw new Error(`Cannot load product ${handle}`);
    product.variants = variants;
    return product;
  };

  const loadProduct = async (handle) => {
    if (!barCache.has(handle)) barCache.set(handle, fetchProduct(handle));
    try {
      return await barCache.get(handle);
    } catch (error) {
      barCache.delete(handle);
      throw error;
    }
  };

  const normalizeValue = (value) => String(value ?? '').trim();

  const optionKey = (value) => normalizeValue(value).toLowerCase().replace(/\s+/g, '');

  const uniq = (arr) => {
    const seen = new Set();
    return arr.map(normalizeValue).filter((value) => {
      const key = optionKey(value);
      if (!value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const findButton = (wrap, value) =>
    Array.prototype.find.call(wrap.querySelectorAll('.addon-btn'), btn => btn.dataset.value === value);

  const makeBtn = (label, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'addon-btn';
    b.textContent = normalizeValue(label);
    b.dataset.value = normalizeValue(label);
    b.addEventListener('click', onClick);
    return b;
  };

  const setActive = (wrap, value) => {
    wrap.querySelectorAll('.addon-btn').forEach(btn => {
      const active = btn.dataset.value === value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  };

  const matchingValue = (values, savedValue) => {
    if (!savedValue) return null;
    const savedKey = optionKey(savedValue);
    return values.find(value => optionKey(value) === savedKey) || null;
  };

  // Get or create a hidden input directly inside the product form.
  // These inputs live OUTSIDE <variant-picker> so they survive re-renders.
  const ensureFormInput = (form, dataAttr, name) => {
    let el = form.querySelector(`[${dataAttr}]`);
    if (!el) {
      el = document.createElement('input');
      el.type  = 'hidden';
      el.name  = name;
      el.value = '';
      el.setAttribute(dataAttr, '');
      form.appendChild(el);
    }
    return el;
  };

  // ── init (idempotent: skips if widget already has .is-ready) ───────────────
  const init = async () => {

    const root = document.querySelector('[data-set-addons]');
    if (!root || root.classList.contains('is-ready') || root.classList.contains('is-initializing')) return;

    const form = root.closest('form') || document.querySelector('.js-product-form-main, form[action*="/cart/add"]');
    if (!form) return;

    root.classList.add('is-initializing', 'is-ready');

    try {
      // Persistent hidden inputs live in the form, so line-item properties submit to cart and checkout.
      const seatTitle = ensureFormInput(form, 'data-seatpost-title',       'properties[Tija]');
      const barTitle  = ensureFormInput(form, 'data-handlebar-title',      'properties[Manillar]');
      const barVarId  = ensureFormInput(form, 'data-handlebar-variant-id', 'properties[_Manillar ID]');
      const barHandle = ensureFormInput(form, 'data-handlebar-handle-value', 'properties[_Manillar Handle]');

      // ── Seatpost ──────────────────────────────────────────────────────────
      const seatLengthWrap = root.querySelector('[data-seatpost-length]');
      const seatOffsetWrap = root.querySelector('[data-seatpost-offset]');
      const seatWrap = root.querySelector('[data-seatpost-options]');

      if (seatLengthWrap && seatOffsetWrap) {
        const lengthButtons = Array.from(seatLengthWrap.querySelectorAll('.addon-btn'));
        const offsetButtons = Array.from(seatOffsetWrap.querySelectorAll('.addon-btn'));
        if (!lengthButtons.length || !offsetButtons.length) return;

        const savedParts = normalizeValue(savedSeatValue).split('·').map(normalizeValue);
        let selectedLength = findButton(seatLengthWrap, savedParts[0])?.dataset.value || lengthButtons[0].dataset.value;
        let selectedOffset = findButton(seatOffsetWrap, savedParts[1])?.dataset.value || offsetButtons[0].dataset.value;

        const syncSeatpost = () => {
          setActive(seatLengthWrap, selectedLength);
          setActive(seatOffsetWrap, selectedOffset);
          const combined = `${selectedLength} · ${selectedOffset}`;
          seatTitle.value = combined;
          savedSeatValue = combined;
        };

        lengthButtons.forEach(btn => {
          btn.dataset.value = normalizeValue(btn.dataset.value);
          btn.addEventListener('click', () => {
            selectedLength = btn.dataset.value;
            syncSeatpost();
          });
        });
        offsetButtons.forEach(btn => {
          btn.dataset.value = normalizeValue(btn.dataset.value);
          btn.addEventListener('click', () => {
            selectedOffset = btn.dataset.value;
            syncSeatpost();
          });
        });
        syncSeatpost();
      } else if (seatWrap) {
        const preRendered = seatWrap.querySelectorAll('.addon-btn');
        if (preRendered.length > 0) {
          // Legacy combined control — retained for any cached/older section markup.
          preRendered.forEach(btn => {
            btn.dataset.value = normalizeValue(btn.dataset.value || btn.textContent);
            btn.textContent = btn.dataset.value;
            btn.addEventListener('click', () => {
              setActive(seatWrap, btn.dataset.value);
              seatTitle.value = btn.dataset.value;
              savedSeatValue  = btn.dataset.value;
            });
          });

          const saved = savedSeatValue && findButton(seatWrap, savedSeatValue);
          const selected = (saved || preRendered[0])?.dataset.value;
          if (selected) {
            setActive(seatWrap, selected);
            seatTitle.value = selected;
            savedSeatValue = selected;
          }
        } else {
          // Dynamic fallback: render from Shopify product JSON
          const seatpostHandle = root.dataset.seatpostHandle;
          if (seatpostHandle) {
            const seatpost = await fetchProduct(seatpostHandle);
            uniq(seatpost.variants.map(v => v.option1)).forEach(len => {
              seatWrap.appendChild(makeBtn(len, () => {
                setActive(seatWrap, len);
                seatTitle.value = len;
                savedSeatValue  = len;
              }));
            });

            const saved = savedSeatValue && findButton(seatWrap, savedSeatValue);
            const selected = (saved || seatWrap.querySelector('.addon-btn'))?.dataset.value;
            if (selected) {
              setActive(seatWrap, selected);
              seatTitle.value = selected;
              savedSeatValue = selected;
            }
          }
        }
      } else {
        return;
      }

      // ── Handlebar ─────────────────────────────────────────────────────────
      const barModelWrap = root.querySelector('[data-bar-model]');
      const barWidthWrap = root.querySelector('[data-bar-width]');
      const barStemWrap  = root.querySelector('[data-bar-stem]');
      if (!barWidthWrap || !barStemWrap) return;

      const defaultHandle = root.dataset.handlebarHandle;
      const modelButtons = barModelWrap
        ? Array.from(barModelWrap.querySelectorAll('.addon-btn:not([data-no-handlebar])'))
        : [];

      // "Sin manillar" is a real variant option (Manillar: Incluido / Sin manillar, cheaper set).
      // Its native radio row is hidden by variant-picker.liquid; we drive it by option value ID.
      const noBarBtn = barModelWrap ? barModelWrap.querySelector('[data-no-handlebar]') : null;
      const fitRows = Array.from(root.querySelectorAll('[data-bar-fit]'));
      const noBarRadio = () => findNoBarRadio(root);
      const withBarRadio = () => {
        const r = noBarRadio();
        if (!r) return null;
        const group = (r.closest('fieldset, .option-selector') || document).querySelectorAll('.js-option');
        return Array.prototype.find.call(group, input => input !== r && input.name === r.name) || null;
      };
      const pick = (radio) => {
        if (!radio || radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      };
      const availableHandles = modelButtons
        .map(btn => normalizeValue(btn.dataset.value))
        .filter(Boolean);
      if (defaultHandle && !availableHandles.includes(defaultHandle)) {
        availableHandles.unshift(defaultHandle);
      }
      if (!availableHandles.length) return;

      let renderToken = 0;
      const showNoBar = () => {
        renderToken += 1; // drop any handlebar render still in flight
        if (barModelWrap) setActive(barModelWrap, 'none');
        fitRows.forEach(row => { row.hidden = true; });
        barWidthWrap.replaceChildren();
        barStemWrap.replaceChildren();
        barVarId.value = '';
        barHandle.value = '';
        barTitle.value = NO_BAR_LABEL;
      };
      const renderHandlebar = async (handle) => {
        const token = ++renderToken;
        savedBarHandle = handle;
        fitRows.forEach(row => { row.hidden = false; });
        if (barModelWrap) setActive(barModelWrap, handle);
        barWidthWrap.replaceChildren();
        barStemWrap.replaceChildren();
        barWidthWrap.setAttribute('aria-busy', 'true');
        barStemWrap.setAttribute('aria-busy', 'true');

        const bar = await loadProduct(handle);
        if (token !== renderToken) return;

        // Option lists define the complete fit range; fetchProduct paginates the
        // variants so each width/stem selection also resolves its exact ID.
        const optionObjs = bar.options || [];
        const optNames = optionObjs.map(option =>
          String(option && option.name != null ? option.name : option).toLowerCase()
        );
        const wRaw = optNames.findIndex(name => name.includes('ancho') || name.includes('width'));
        const wIdx = wRaw >= 0 ? wRaw : 0;
        const sRaw = optNames.findIndex(name =>
          name.includes('potencia') || name.includes('stem') ||
          name.includes('longitud') || name.includes('length')
        );
        const sIdx = sRaw >= 0 ? sRaw : 1;
        const aIdx = optNames.findIndex(name =>
          name.includes('espaciador') || name.includes('adapter') || name.includes('adaptador')
        );
        const getOpt = (variant, index) => [variant.option1, variant.option2, variant.option3][index];
        const optValues = (index) => (
          optionObjs[index] && Array.isArray(optionObjs[index].values)
            ? optionObjs[index].values
            : null
        );
        const widths = uniq(optValues(wIdx) || bar.variants.map(variant => getOpt(variant, wIdx)));
        const stems = uniq(optValues(sIdx) || bar.variants.map(variant => getOpt(variant, sIdx)));
        const saved = savedBarSelections.get(handle) || {};
        let selWidth = matchingValue(widths, saved.width) || widths[0];
        let selStem = matchingValue(stems, saved.stem) || stems[0];

        const syncBar = () => {
          const variant = bar.variants.find(candidate => {
            const sizeMatches = optionKey(getOpt(candidate, wIdx)) === optionKey(selWidth) &&
              optionKey(getOpt(candidate, sIdx)) === optionKey(selStem);
            const noAdapter = aIdx < 0 || /^sin\b/i.test(normalizeValue(getOpt(candidate, aIdx)));
            return sizeMatches && noAdapter && candidate.available !== false;
          });
          const modelButton = modelButtons.find(btn => btn.dataset.value === handle);
          const modelName = normalizeValue(bar.title) || normalizeValue(modelButton?.textContent) || handle;
          barVarId.value = variant ? String(variant.id) : '';
          barHandle.value = handle;
          barTitle.value = `${modelName} · Ancho: ${selWidth} · Potencia: ${selStem}`;
          savedBarSelections.set(handle, { width: selWidth, stem: selStem });
        };

        widths.forEach(width => barWidthWrap.appendChild(makeBtn(width, () => {
          selWidth = width;
          setActive(barWidthWrap, width);
          syncBar();
        })));
        stems.forEach(stem => barStemWrap.appendChild(makeBtn(stem, () => {
          selStem = stem;
          setActive(barStemWrap, stem);
          syncBar();
        })));

        barWidthWrap.removeAttribute('aria-busy');
        barStemWrap.removeAttribute('aria-busy');
        setActive(barWidthWrap, selWidth);
        setActive(barStemWrap, selStem);
        syncBar();
      };

      modelButtons.forEach(btn => {
        btn.dataset.value = normalizeValue(btn.dataset.value);
        btn.addEventListener('click', () => {
          pick(withBarRadio()); // back to the set WITH handlebar
          renderHandlebar(btn.dataset.value).catch(error => {
            console.error('SET ADDONS handlebar switch failed:', error);
          });
        });
      });

      if (noBarBtn) {
        if (!noBarRadio()) {
          noBarBtn.remove(); // fail closed: no matching variant option on this page
        } else {
          noBarBtn.addEventListener('click', () => {
            showNoBar();
            pick(noBarRadio()); // switches the product to the "Sin manillar" variant (and its price)
          });
        }
      }

      const initialHandle = availableHandles.includes(savedBarHandle)
        ? savedBarHandle
        : (availableHandles.includes(defaultHandle) ? defaultHandle : availableHandles[0]);
      if (noBarRadio()?.checked) {
        savedBarHandle = initialHandle;
        showNoBar();
      } else {
        await renderHandlebar(initialHandle);
      }

      // ── Form validation (attached once; form persists across variant changes) ───
      if (!form.dataset.setAddonsListened) {
        form.dataset.setAddonsListened = 'true';
        form.addEventListener('submit', (e) => {
          const st = form.querySelector('[data-seatpost-title]');
          const bt = form.querySelector('[data-handlebar-title]');
          const bi = form.querySelector('[data-handlebar-variant-id]');
          const r = document.querySelector('[data-set-addons]');
          // Without handlebar only when the cheaper variant is really the one being added.
          const noBar = r && findNoBarRadio(r)?.checked && bt?.value === NO_BAR_LABEL;
          if (!st?.value.trim() || !bt?.value.trim() || (!noBar && !bi?.value.trim())) {
            e.preventDefault();
            const r = document.querySelector('[data-set-addons]');
            alert(r?.dataset.alertMsg || 'Selecciona manillar y tija antes de añadir al carrito.');
          }
        });
      }

    } catch (err) {
      console.error('SET ADDONS init failed:', err);
      document.querySelector('[data-set-addons]')?.style.setProperty('display', 'none');
    } finally {
      root.classList.remove('is-initializing');
    }
  };

  // ── boot ───────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {

    init();

    // Re-init whenever a variant / colour change re-renders [data-set-addons].
    // Debounced so our own appendChild calls during init() don’t trigger a loop.
    new MutationObserver(() => {

      clearTimeout(reinitTimer);
      reinitTimer = setTimeout(() => {
        if (document.querySelector('[data-set-addons]:not(.is-ready):not(.is-initializing)')) init();
      }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
